import { useContext } from 'react';
import { generatePath } from 'react-router';

import { AkaThemaId } from '../dtos';
import { BackendConfigContext } from '../utils';

export const PATH = {
  ROOT: '/',
  AUTH: {
    ROOT: '/auth',
    LOGOUT: '/auth/logout',
    REDIRECT: '/auth/redirect',
  },
  FEHLER: '/fehler',
  DATENSCHUTZ: '/datenschutz',
  RAUM_RESSOURCEN_UEBERSICHT: '/raumressourcenuebersicht',
  THEMA: {
    DETAILS: '/thema/:akaThemaId',
    DETAILS_REDIRECT_BY_AKA_PRODUKT_ID: '/thema/redirect/:akaProduktId',
    VERANSTALTUNG_PLANUNG: '/thema/:akaThemaId/veranstaltung/neu/planung',
    DURCHGANG_PLANUNG: '/thema/:akaThemaId/durchgang-planung',
  },
  VERANSTALTUNG: {
    DETAILS: '/veranstaltung/:akaVeranstaltungId',
  },
  DURCHGANG: {
    DETAILS: '/durchgang/:akaDurchgangId',
  },
  NOT_FOUND: '*',
} as const;

export const url = {
  root: (): string => generatePath(PATH.ROOT),
  auth: {
    logout: (): string => generatePath(PATH.AUTH.LOGOUT),
  },
  fehler: (): string => generatePath(PATH.FEHLER),
  datenschutz: (): string => generatePath(PATH.DATENSCHUTZ),
  raumRessourcenUebersicht: (): string => generatePath(PATH.RAUM_RESSOURCEN_UEBERSICHT),
  thema: {
    details: (akaThemaId: AkaThemaId): string => generatePath(PATH.THEMA.DETAILS, { akaThemaId }),
    detailsRedirectByAkaProduktId: (akaProduktId: string): string =>
      generatePath(PATH.THEMA.DETAILS_REDIRECT_BY_AKA_PRODUKT_ID, { akaProduktId }),
    veranstaltungPlanung: (akaThemaId: AkaThemaId): string =>
      generatePath(PATH.THEMA.VERANSTALTUNG_PLANUNG, { akaThemaId }),
    durchgangPlanung: (akaThemaId: AkaThemaId): string => generatePath(PATH.THEMA.DURCHGANG_PLANUNG, { akaThemaId }),
  },
  veranstaltung: {
    details: (akaVeranstaltungId: string, veranstaltungSapId?: number | null): string =>
      veranstaltungSapId
        ? `${generatePath(PATH.VERANSTALTUNG.DETAILS, { akaVeranstaltungId })}?sapId=${veranstaltungSapId}`
        : generatePath(PATH.VERANSTALTUNG.DETAILS, { akaVeranstaltungId }),
  },
  durchgang: {
    details: (akaDurchgangId: string): string => generatePath(PATH.DURCHGANG.DETAILS, { akaDurchgangId }),
    detailsForModultermin: (akaDurchgangId: string, akaVeranstaltungId: string): string =>
      generatePath(PATH.DURCHGANG.DETAILS, { akaDurchgangId }) + '#' + akaVeranstaltungId,
  },
} as const;

type LeistungWithOptionalAusschreibungLink = {
  leistungId: string;
  ausschreibung: { ausschreibungsportalLink: string | null } | null;
};

/**
 * This hook provides URL creator functions that depend on base URLs configured in BackendConfigContext
 */
export function useConfiguredUrl() {
  const {
    HiveAppUrl: hiveBaseURL,
    SapAdapterFrontendUrl: sapAdapterBaseURL,
    AusschreibungsportalUrl: ausschreibungsportalBaseURL,
  } = useContext(BackendConfigContext);

  return {
    configuredUrl: {
      hive: {
        experte: {
          details: (experteSapId: number): string => `${hiveBaseURL}/experten/${experteSapId}`,
          auslastung: (experteSapId: number): string => `${hiveBaseURL}/experten/${experteSapId}/auslastung`,
        },
        gesellschaft: (gesellschaftId: string): string => `${hiveBaseURL}/organizations/${gesellschaftId}`,
      },
      sapAdapter: {
        root: (): string => sapAdapterBaseURL,
        veranstaltung: (veranstaltungId: number): string => `${sapAdapterBaseURL}/veranstaltungen/${veranstaltungId}`,
        thema: (akaProduktId: string): string => `${sapAdapterBaseURL}/themen/${akaProduktId}`,
      },
      ausschreibungsportal: {
        ausschreibungByLeistungId: (leistungId: string): string =>
          `${ausschreibungsportalBaseURL}/leistung/${leistungId}`,
        ausschreibungByLeistung: (leistung: LeistungWithOptionalAusschreibungLink): string =>
          leistung.ausschreibung?.ausschreibungsportalLink ??
          `${ausschreibungsportalBaseURL}/leistung/${leistung.leistungId}`,
      },
    },
  } as const;
}
